import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule } from '@jsverse/transloco';
import { AnimalsService } from '@anihis/core/data-access';

const CLIENT_APP_DEEP_LINK = 'com.anihis.app://dashboard';
const APP_STORE_URL = 'https://apps.apple.com/app/anihis/id0000000000';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.anihis.app';
const COUNTDOWN_SECONDS = 5;

type ConfirmStatus = 'loading' | 'success' | 'error';

@Component({
  selector: 'anihis-confirm-transfer',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, TranslocoModule],
  templateUrl: './confirm-transfer.component.html',
  styleUrl: './confirm-transfer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmTransferComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private animalsService = inject(AnimalsService);

  status = signal<ConfirmStatus>('loading');
  countdown = signal(COUNTDOWN_SECONDS);
  errorMessage = signal('');

  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const transferUid = params['transferUid'] ?? '';
      const token = params['token'] ?? '';
      this.confirmTransfer(transferUid, token);
    });
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  openApp(): void {
    window.location.href = CLIENT_APP_DEEP_LINK;
  }

  getAppStoreUrl(): string {
    const ua = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(ua) ? APP_STORE_URL : PLAY_STORE_URL;
  }

  private confirmTransfer(transferUid: string, token: string): void {
    if (!transferUid || !token) {
      this.errorMessage.set(
        'Invalid transfer link. Please check the email and try again.',
      );
      this.status.set('error');
      return;
    }

    this.animalsService
      .animalsTransferConfirmPost({ transferUid, token })
      .subscribe({
        next: () => {
          this.status.set('success');
          this.startCountdown();
        },
        error: (err: {
          error?: {
            errors?: Record<string, string[]>;
            detail?: string;
            title?: string;
          };
        }) => {
          const errors = err?.error?.errors;
          const msg = errors
            ? Object.values(errors).flat().join(' ')
            : err?.error?.detail ||
              err?.error?.title ||
              'Transfer confirmation failed. The link may have expired or already been used.';
          this.errorMessage.set(msg);
          this.status.set('error');
        },
      });
  }

  private startCountdown(): void {
    this.countdownInterval = setInterval(() => {
      const next = this.countdown() - 1;
      this.countdown.set(next);
      if (next <= 0) {
        clearInterval(this.countdownInterval!);
        this.countdownInterval = null;
        window.location.href = CLIENT_APP_DEEP_LINK;
      }
    }, 1000);
  }
}
